import React, { useEffect, useState } from "react";
import { matchPath, useLocation } from "react-router-dom";

import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ModeEditOutlineOutlinedIcon from "@mui/icons-material/ModeEditOutlineOutlined";
import { Box, Collapse, Divider, IconButton, Stack } from "@mui/material";
import { DealRoleGuard, RoleComponentGuard, SharedDealGuard } from "components/guards";
import { Routes } from "constants/routes";
import {
  useAccountSelector,
  useDealActions,
  useDealSelector,
  useIsSharedDealSelect,
  usePickUserPermissions,
  useUserRoleSelector
} from "store";
import { ICharacteristicViewData, RoutesValues, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { RoleName } from "../../../constants";
import { CopySharedDeal } from "../Copy-shared-deal/CopySharedDeal";
import { Crunchbase } from "../Crunchbase/Crunchbase";
import { Cryptocurrency } from "../Cryptocurrency/Cryptocurrency";
import { DeleteSharedDeal } from "../Delete-shared-deal/DeleteSharedDeal";
import { Fees } from "../Fees/Fees";
import { Logo } from "../Logo/Logo";
import { Name } from "../Name/Name";
import { Asset } from "./Asset/Asset";
import { CharacteristicsList } from "./CharacteristicsList/CharacteristicsList";
import { Contact } from "./Contact/Contact";
import { Currency } from "./Currency/Currency";
import { Geography } from "./Geography/Geography";
import { Investors } from "./investors/Investors";
import { Rating } from "./Rating/Rating";
import { Vintage } from "./Vintage/Vintage";

interface ICharacteristicsBarProps {
  list: ICharacteristicViewData;
  refetch: () => Promise<void>;
  missingFieldIds?: number[];
  missingFieldNames?: string[];
  requiredForStageId?: string;
}

const openCharBarRoutes: RoutesValues[] = [Routes.dealDashboard];

export const CharacteristicsBar: React.FC<ICharacteristicsBarProps> = ({ list, refetch, missingFieldNames }) => {
  const isSharedDeal = useIsSharedDealSelect();
  const { pathname } = useLocation();
  const { setCharacteristicsManageAction } = useDealActions();
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(false);

  const myDeals = usePickUserPermissions("deals");
  const myCharacteristics = usePickUserPermissions("characteristics");
  const noAccessPermission = myDeals && myDeals === UserPermissionType.NO_ACCESS;
  const isViewerPermission = myDeals && myDeals === UserPermissionType.VIEWER;
  const noAccessCharacteristics = myCharacteristics && myCharacteristics === UserPermissionType.NO_ACCESS;
  const isCharacteristicsViewerPermission = myCharacteristics && myCharacteristics === UserPermissionType.VIEWER;

  const role = useUserRoleSelector();
  const { enable_crypto, show_debt } = useAccountSelector() || {};

  const { id, title } = useDealSelector();

  const isAdmin = isRoleAnalyst({ role, preventTV: true, preventUser: noAccessPermission || isViewerPermission });

  useEffect(() => {
    const shouldBeOpen = openCharBarRoutes.some((route) => matchPath(pathname, route)) || !!missingFieldNames?.length;

    setIsPanelOpen(shouldBeOpen);
  }, [pathname, missingFieldNames?.length]);

  return (
    <Box
      sx={{
        borderRadius: "8px",
        bgcolor: "white",
        marginBottom: "16px",
        boxShadow: "0 1px 2px 0 #1212171a",
        transition: "0.5s",
        overflow: "hidden"
      }}
    >
      <Stack direction="row" justifyContent="space-between" sx={{ padding: "16px" }}>
        <Stack direction="row" gap="10px" alignItems="center" flexWrap="wrap">
          <SharedDealGuard>
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer]}>
              <IconButton onClick={() => setIsPanelOpen((prev) => !prev)}>
                <KeyboardArrowRightIcon sx={{ transform: isPanelOpen ? "rotate(90deg)" : "none", cursor: "pointer" }} />
              </IconButton>
            </DealRoleGuard>
          </SharedDealGuard>
          <SharedDealGuard>
            <DealRoleGuard availableRoles={[RoleName.Guest, RoleName.Viewer]}>
              <Logo />
              <Divider sx={{ height: "16px" }} orientation="vertical" variant="middle" light />
            </DealRoleGuard>
          </SharedDealGuard>
          <Name />
          <SharedDealGuard>
            {!noAccessCharacteristics && <Asset isAdmin={isAdmin} disablePopup={isCharacteristicsViewerPermission} />}
          </SharedDealGuard>
          <SharedDealGuard>
            <DealRoleGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={noAccessPermission || isViewerPermission}
            >
              {/* <Commitment /> */}
              <Investors />
            </DealRoleGuard>
          </SharedDealGuard>
          <SharedDealGuard>
            <Contact role={role} />
          </SharedDealGuard>
          {!noAccessCharacteristics && (
            <Vintage
              isAdmin={isAdmin}
              disablePopup={isSharedDeal || isCharacteristicsViewerPermission}
              onSuccess={refetch}
              dealId={id}
            />
          )}
          {!noAccessCharacteristics && (
            <Geography
              isAdmin={isAdmin}
              disablePopup={isSharedDeal || isCharacteristicsViewerPermission}
              onSuccess={refetch}
              dealId={id}
            />
          )}
          {!noAccessCharacteristics && (
            <Currency
              isAdmin={isAdmin}
              disablePopup={isSharedDeal || isCharacteristicsViewerPermission}
              onSuccess={refetch}
              dealId={id}
            />
          )}
          {show_debt && !noAccessCharacteristics && (
            <Rating
              isAdmin={isAdmin}
              disablePopup={isSharedDeal || isCharacteristicsViewerPermission}
              onCreateSubmit={refetch}
            />
          )}
          <SharedDealGuard>
            <DealRoleGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
              <IconButton
                sx={{
                  fontSize: "15px",
                  backgroundColor: "#edf1f9"
                }}
                onClick={() =>
                  setCharacteristicsManageAction({
                    dealName: title,
                    dealId: id,
                    viewOnly: noAccessPermission || isViewerPermission,
                    onSubmitSuccess: refetch
                  })
                }
              >
                <ModeEditOutlineOutlinedIcon />
              </IconButton>
            </DealRoleGuard>
          </SharedDealGuard>
        </Stack>
        <SharedDealGuard>
          <Stack flexDirection="row" gap="20px">
            <RoleComponentGuard
              availableRoles={[RoleName.Admin, RoleName.Analyst]}
              hideChild={noAccessPermission || isViewerPermission}
            >
              <Fees />
              {enable_crypto && <Cryptocurrency />}
              <Crunchbase />
            </RoleComponentGuard>
          </Stack>
        </SharedDealGuard>
        <SharedDealGuard reverse>
          <Stack direction="row" sx={{ gap: "18px" }}>
            <DeleteSharedDeal id={id} />
            <CopySharedDeal id={id} />
          </Stack>
        </SharedDealGuard>
      </Stack>
      <SharedDealGuard>
        <Collapse in={isPanelOpen} timeout={500} unmountOnExit>
          <CharacteristicsList list={list} refetch={refetch} missingFieldNames={missingFieldNames} />
        </Collapse>
      </SharedDealGuard>
    </Box>
  );
};
